import { useCallback, useEffect, useRef, useState } from "react";

import { getUsage } from "../api/usage";
import { useAuth } from "../contexts/AuthContext";

/**
 * Fetch per-feature monthly usage for the logged-in user.
 *
 * Returns a map keyed by feature name (e.g. "prompt_tests") plus a `refresh` callback — call it
 * after a generate succeeds or returns 403 to keep the displayed counts current. No-ops (returns
 * null) for anonymous users, who are not metered server-side.
 *
 * @returns {{
 *   byFeature: Object | null;
 *   loading: boolean;
 *   error: string | null;
 *   refresh: () => Promise<Object | null>;
 * }}
 */
export function useUsage() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [byFeature, setByFeature] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    const requestGeneration = ++generation.current;
    if (!userId) {
      setByFeature(null);
      setError(null);
      return null;
    }
    setLoading(true);
    try {
      const res = await getUsage();
      const map = {};
      for (const feature of res.data.features) {
        map[feature.feature] = feature;
      }
      if (requestGeneration !== generation.current) return null;
      setByFeature(map);
      setError(null);
      return map;
    } catch (err) {
      if (requestGeneration !== generation.current) return null;
      console.error("usage fetch failed", err);
      setByFeature(null);
      setError("Usage could not be loaded.");
      return null;
    } finally {
      if (requestGeneration === generation.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setByFeature(null);
    setError(null);
    refresh();
  }, [refresh]);

  return { byFeature, loading, error, refresh };
}
